// Web Portal Notifications template-binding service.
// CRUD for the per-send_type WhatsApp template config shown in /actions (card 07).
// Backend: /api/v1/campaigns/template-config/ (GET both rows, PUT upsert by send_type).

import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'

const BASE = '/api/v1/campaigns/template-config'

export type WebPortalSendType =
  | 'search_alert_match'
  | 'price_drop'
  | 'rising_offer_received'
  | 'rising_offer_updated'
  | 'rising_offer_winner'
  | 'rising_offer_not_winner'
  | 'buyer_reactivation_check'
  | 'auth_otp'

export type WebPortalVariableMappingEntry = {
  position: number
  source_type: 'variable'
  source_key: string
  fallback_text?: string
}

export type WebPortalAvailableField = {
  key: string
  label: string
  example?: string
}

// Only meaningful for send_types whose template has quick-reply buttons
// (currently buyer_reactivation_check): maps our internal "yes"/"no" to the
// button's own text as approved in Meta.
export type WebPortalButtonResponseMapping = Partial<Record<'yes' | 'no', string>>

export type WebPortalTemplateConfig = {
  send_type: WebPortalSendType
  template_name: string
  template_language: string
  waba_id: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  button_response_mapping: WebPortalButtonResponseMapping
  expected_body_params: number
  available_fields: WebPortalAvailableField[]
  updated_at: string | null
}

export type WebPortalTemplateConfigInput = {
  send_type: WebPortalSendType
  template_name: string
  template_language: string
  waba_id: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  button_response_mapping?: WebPortalButtonResponseMapping
}

class WebPortalTemplatesServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = getConversationsAuthToken()
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token ${token}` } : {}),
        ...options.headers,
      },
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      const detail =
        (payload as { error?: string }).error ||
        (payload as { detail?: string }).detail ||
        response.statusText
      throw new Error(detail || `HTTP ${response.status}`)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  getConfig() {
    return this.request<{ success: boolean; data: WebPortalTemplateConfig[] }>(`${BASE}/`)
  }

  putConfig(input: WebPortalTemplateConfigInput) {
    return this.request<{ success: boolean; data: WebPortalTemplateConfig }>(`${BASE}/`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  }
}

export const webPortalTemplatesService = new WebPortalTemplatesServiceClient()
